// src/hooks/useProjects.js
import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "../supabaseClient";

const getActiveTenantId = () => {
  const tenantId = window.localStorage.getItem("activeTenantId");
  return tenantId ? String(tenantId).trim() : "";
};

export function useProjects(user) {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [favouriteMutationProjectIds, setFavouriteMutationProjectIds] = useState(new Set());
  const favouriteMutationInFlightRef = useRef(new Set());
  const selectedProjectIdRef = useRef(null);
  const [activeTenantId, setActiveTenantId] = useState(getActiveTenantId());

  useEffect(() => {
    selectedProjectIdRef.current = selectedProjectId;
  }, [selectedProjectId]);

  useEffect(() => {
    const handleTenantChanged = (event) => {
      const nextTenantId = String(event?.detail?.tenantId || "").trim();
      setActiveTenantId(nextTenantId);
    };

    const handleStorageChanged = (event) => {
      if (event.key === "activeTenantId") {
        setActiveTenantId(String(event.newValue || "").trim());
      }
    };

    window.addEventListener("active-tenant-changed", handleTenantChanged);
    window.addEventListener("storage", handleStorageChanged);

    return () => {
      window.removeEventListener("active-tenant-changed", handleTenantChanged);
      window.removeEventListener("storage", handleStorageChanged);
    };
  }, []);

  const fetchProjects = useCallback(async () => {
    if (!user) {
      setProjects([]);
      return;
    }

    if (!activeTenantId) {
      setProjects([]);
      return;
    }

    const { data, error } = await supabase
      .from("project_access")
      .select("projects(*)")
      .eq("user_id", user.id)
      .eq("tenant_id", activeTenantId);

    if (error) {
      // console.error("Error fetching projects:", error);
      return;
    }

    const projectList = data
      .map((entry) => entry.projects)
      .filter((project) => project && project.is_archive !== true);
    const projectIds = projectList.map((project) => project.id);

    let favouriteIdSet = new Set();
    if (projectIds.length > 0) {
      const { data: favouritesData, error: favouritesError } = await supabase
        .from("user_project_favourites")
        .select("project_id")
        .eq("user_id", user.id)
        .eq("tenant_id", activeTenantId)
        .in("project_id", projectIds);

      if (!favouritesError) {
        favouriteIdSet = new Set((favouritesData || []).map((row) => row.project_id));
      }
    }

    const normalizedProjects = projectList
      .map((project) => ({
        ...project,
        is_favourite: favouriteIdSet.has(project.id),
      }))
      .sort((a, b) => {
        if (a.is_favourite !== b.is_favourite) {
          return a.is_favourite ? -1 : 1;
        }
        return String(a.name || "").localeCompare(String(b.name || ""));
      });

    setProjects(normalizedProjects);

    const currentSelectedProjectId = selectedProjectIdRef.current;
    if (currentSelectedProjectId && !normalizedProjects.some((project) => project.id === currentSelectedProjectId)) {
      setSelectedProjectId(null);
    }
  }, [user, activeTenantId]);

  useEffect(() => {
    if (user && activeTenantId) {
      fetchProjects();
      return;
    }

    setProjects([]);
  }, [user, fetchProjects]);

  const toggleProjectFavourite = async (projectId) => {
    if (!user || !projectId) return;

    const normalizedProjectId = String(projectId);
    if (favouriteMutationInFlightRef.current.has(normalizedProjectId)) {
      return;
    }

    const currentProject = projects.find((project) => String(project.id) === normalizedProjectId);
    if (!currentProject) return;

    const nextIsFavourite = !currentProject.is_favourite;
    favouriteMutationInFlightRef.current.add(normalizedProjectId);

    setProjects((prev) =>
      prev.map((project) =>
        String(project.id) === normalizedProjectId
          ? { ...project, is_favourite: nextIsFavourite }
          : project
      )
    );
    setFavouriteMutationProjectIds((prev) => {
      const next = new Set(prev);
      next.add(normalizedProjectId);
      return next;
    });

    try {
      let error = null;
      if (nextIsFavourite) {
        const activeTenantId = getActiveTenantId();
        if (!activeTenantId) {
          error = new Error("No active tenant selected.");
        } else {
          const existingResult = await supabase
            .from("user_project_favourites")
            .select("project_id")
            .eq("user_id", user.id)
            .eq("tenant_id", activeTenantId)
            .eq("project_id", normalizedProjectId)
            .maybeSingle();

          if (existingResult.error) {
            error = existingResult.error;
          } else if (!existingResult.data) {
            const insertResult = await supabase
              .from("user_project_favourites")
              .insert({
                user_id: user.id,
                project_id: normalizedProjectId,
                tenant_id: activeTenantId,
              });

            const isDuplicate = insertResult.error && String(insertResult.error.code || "") === "23505";
            error = isDuplicate ? null : insertResult.error;
          } else {
            error = null;
          }
        }
      } else {
        const activeTenantId = getActiveTenantId();
        if (!activeTenantId) {
          error = new Error("No active tenant selected.");
        } else {
          const result = await supabase
            .from("user_project_favourites")
            .delete()
            .eq("user_id", user.id)
            .eq("tenant_id", activeTenantId)
            .eq("project_id", normalizedProjectId);
          error = result.error;
        }
      }

      if (error) {
        await fetchProjects();
      } else {
        setProjects((prev) =>
          [...prev].sort((a, b) => {
            if (a.is_favourite !== b.is_favourite) {
              return a.is_favourite ? -1 : 1;
            }
            return String(a.name || "").localeCompare(String(b.name || ""));
          })
        );
      }
    } finally {
      favouriteMutationInFlightRef.current.delete(normalizedProjectId);
      setFavouriteMutationProjectIds((prev) => {
        const next = new Set(prev);
        next.delete(normalizedProjectId);
        return next;
      });
    }
  };

  const addProject = (newProject) => {
    setProjects((prev) => [...prev, newProject]);
  };

  return {
    projects,
    setProjects,
    selectedProjectId,
    setSelectedProjectId,
    addProject,
    reloadProjects: fetchProjects,
    toggleProjectFavourite,
    favouriteMutationProjectIds,
  };
}
